import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Card,
  Button,
  Divider,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import api from "../utils/api";
import WorkIcon from "@mui/icons-material/Work";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { keyframes } from "@emotion/react";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -58%) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const OrdersPreview = () => {
  const [myWorkOrders, setMyWorkOrders] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const getCurrentUserId = () => {
    const currentUserId = localStorage.getItem("currentUserId");
    if (currentUserId) return currentUserId;

    const userId = localStorage.getItem("userId");
    if (userId) return userId;

    try {
      const storedUser = localStorage.getItem("user");
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      return parsed?.userId || parsed?.id || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const id = getCurrentUserId();

    if (id) {
      fetchUserWorkOrders(id);
    } else {
      console.warn("No user found in localStorage or missing userId.");
    }
  }, []);

  const fetchUserWorkOrders = async (userId) => {
    try {
      setLoading(true);

      const res = await api.get("/getWorkOrders");

      const assigned = (res.data || [])
        .filter((order) => {
          const isAssignedToMe =
            String(order.assign_to) === String(userId) ||
            String(order.assignee_id) === String(userId) ||
            String(order.assigned_user_id) === String(userId);

          const status = String(order.status || order.status_name || "")
            .trim()
            .toLowerCase();

          const isHiddenStatus =
            status === "resolved" ||
            status === "cancel" ||
            status === "cancelled"||
            status === "void";

          return isAssignedToMe && !isHiddenStatus;
        })
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      setMyWorkOrders(assigned);
      setModalOpen(assigned.length > 0);
    } catch (err) {
      console.error("Failed to fetch user work orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const previewOrders = useMemo(() => myWorkOrders.slice(0, 5), [myWorkOrders]);

  const getPriorityColor = (priority) => {
    const value = String(priority || "").toLowerCase();

    if (value.includes("high") || value.includes("urgent")) return "error";
    if (value.includes("medium")) return "warning";
    if (value.includes("low")) return "success";

    return "default";
  };

  const getStatusColor = (status) => {
    const value = String(status || "").toLowerCase();

    if (value.includes("progress")) return "warning";
    if (value.includes("open")) return "info";

    return "default";
  };

  const handleClose = () => setModalOpen(false);

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      sx={{
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(15,23,42,0.38)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 560 },
          maxHeight: "88vh",
          overflow: "hidden",
          bgcolor: "rgba(255,255,255,0.98)",
          borderRadius: "22px",
          boxShadow: "0 24px 80px rgba(15,23,42,0.28)",
          border: "1px solid rgba(47,99,173,0.10)",
          animation: `${fadeIn} 0.25s ease-out`,
        }}
      >
        <Box
          sx={{
            px: { xs: 2.25, sm: 3 },
            pt: 2.5,
            pb: 2,
            background:
              "linear-gradient(135deg, rgba(47,99,173,0.10), rgba(47,99,173,0.025))",
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                backgroundColor: "rgba(47,99,173,0.12)",
              }}
            >
              <AssignmentTurnedInIcon sx={{ color: "#2f63ad" }} />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142" }}>
                Work Orders Assigned to You
              </Typography>

              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                You have {myWorkOrders.length} active assigned work order
                {myWorkOrders.length === 1 ? "" : "s"}.
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              alignSelf: "flex-start",
              backgroundColor: "rgba(255,255,255,0.72)",
              border: "1px solid rgba(47,99,173,0.08)",
              "&:hover": {
                backgroundColor: "rgba(47,99,173,0.08)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            maxHeight: "58vh",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {previewOrders.map((order) => {
                const status = order.status || order.status_name || "Open";
                const priority = order.priority || "Normal";

                return (
                  <Card
                    key={order.order_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <WorkIcon sx={{ color: "#2f63ad", fontSize: 20 }} />

                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, color: "#223142" }}
                          >
                            WO-{order.order_id}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.75,
                            color: "#4b5563",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {order.description || "No description provided."}
                        </Typography>
                      </Box>

                      <Button
                        component={Link}
                        to={`/home/workorders/${order.order_id}`}
                        size="small"
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        sx={{
                          flexShrink: 0,
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 700,
                          backgroundColor: "#2f63ad",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#1f4f94",
                            boxShadow: "none",
                          },
                        }}
                      >
                        View
                      </Button>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 1.5 }}
                    >
                      <Chip
                        size="small"
                        label={status}
                        color={getStatusColor(status)}
                        sx={{ fontWeight: 700, borderRadius: "999px" }}
                      />

                      <Chip
                        size="small"
                        icon={<PriorityHighIcon />}
                        label={`Priority: ${priority}`}
                        color={getPriorityColor(priority)}
                        sx={{ fontWeight: 700, borderRadius: "999px" }}
                      />

                      <Chip
                        size="small"
                        icon={<AccessTimeIcon />}
                        label={
                          order.created_at
                            ? `Created ${new Date(order.created_at).toLocaleDateString()}`
                            : "Created —"
                        }
                        sx={{
                          fontWeight: 600,
                          borderRadius: "999px",
                          backgroundColor: "rgba(107,119,140,0.10)",
                        }}
                      />
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          )}

          {myWorkOrders.length > 5 && (
            <Typography
              variant="body2"
              sx={{ mt: 1.75, color: "#6b778c", textAlign: "center" }}
            >
              Showing 5 of {myWorkOrders.length}. Open Work Orders to see the rest.
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(47,99,173,0.08)" }} />

        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            backgroundColor: "rgba(248,250,252,0.9)",
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              color: "#223142",
              borderColor: "rgba(47,99,173,0.18)",
            }}
          >
            Dismiss
          </Button>

          <Button
            component={Link}
            to="/home/workorders"
            variant="contained"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: "#2f63ad",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1f4f94",
                boxShadow: "none",
              },
            }}
          >
            View All Work Orders
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default OrdersPreview;